import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import AutomationIcon from '@atlaskit/icon/core/automation';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import {
	type ConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';

import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { expVal } from '@confluence/feature-experiments';

import { contentReviewerNudgeI18n } from '../i18n.ts';
import { markDiscoveryExperienceAsSeen } from '../utils/localStorage.ts';
import { resolveDynamicConfigStringValue } from '../utils/prompt-template.ts';

export const CONTENT_REVIEWER_NUDGE_ID = 'automation-discovery-content-reviewer-nudge';
const ROVO_CHAT_SOURCE = CONTENT_REVIEWER_NUDGE_ID;
const DYNAMIC_CONFIG_CONFIG_KEY = 'confluence-content-reviewer-discovery-prompt';

const DEFAULT_CONTENT_REVIEWER_PROMPT = `/build-automation

Trigger: \`PagePublishedTrigger\` - Trigger this automation everytime a Confluence page is published in this space

Action: \`Use Agent (Content Reviewer Agent)\` - Review the page and recommend edits to make the page more engaging, accurate and visually appealing.`;

const CONTENT_REVIEWER_PROMPT_CONFIG = {
	configKey: DYNAMIC_CONFIG_CONFIG_KEY,
	fallback: DEFAULT_CONTENT_REVIEWER_PROMPT,
};

/**
 * Builds the Content Reviewer discovery nudge `ConversationAction` — its title, icon, the Rovo chat
 * it publishes on click, and the localStorage dismissal bookkeeping — given whether the nudge is
 * currently `enabled`.
 */
export const useContentReviewerNudgeAction = (isEnabled: boolean): ConversationAction | null => {
	const { formatMessage } = useIntl();
	const publishRovoChat = usePublish('ai-mate');

	const handleLocalStorageUpdate = useCallback(() => {
		markDiscoveryExperienceAsSeen(keys.AUTOMATION_DISCOVER_CONTENT_REVIEWER);
	}, []);

	return useMemo<ConversationAction | null>(
		() =>
			isEnabled && expVal('confluence-automations-content-reviewer-nudge', 'isEnabled', false)
				? {
						id: CONTENT_REVIEWER_NUDGE_ID,
						title: formatMessage(contentReviewerNudgeI18n.title),
						icon: () => (
							<AutomationIcon
								size="small"
								label={formatMessage(contentReviewerNudgeI18n.iconLabel)}
							/>
						),
						handleType: HandleActionType.Skill,
						type: 'skill-action',
						onClick: () => {
							publishRovoChat({
								type: 'chat-new',
								source: ROVO_CHAT_SOURCE,
								openChat: true,
								openChatMode: 'sidebar',
								data: {
									dialogues: [],
									prompt: resolveDynamicConfigStringValue(CONTENT_REVIEWER_PROMPT_CONFIG),
									placeholderType: 'skill',
									resultAttributionContext: {
										launchSource: ROVO_CHAT_SOURCE,
									},
								},
							});
						},
						nudgeContext: {
							id: CONTENT_REVIEWER_NUDGE_ID,
							selfEligible: true,
							onAccept: handleLocalStorageUpdate,
							onDismiss: (reason) => {
								if (reason?.dismissedMethod === 'manual') {
									handleLocalStorageUpdate();
								}
							},
						},
					}
				: null,
		[formatMessage, handleLocalStorageUpdate, publishRovoChat, isEnabled],
	);
};
